// shop/ui.js — привязка корзины к разметке
// =============================================================================
// Никаких классов оформления в логике: только data-атрибуты, чтобы вёрстку
// партиалов можно было менять, не трогая скрипт.
//
//   [data-cart-count]             — счётчик в шапке
//   [data-add-to-cart][data-slug] — кнопка «В корзину»
//   [data-qty][data-slug]         — обёртка степпера
//     [data-qty-dec] [data-qty-value] [data-qty-inc]
//   [data-cart-remove][data-slug] — «Удалить»
//   [data-cart-lines]             — контейнер строк корзины
//   [data-cart-empty] / [data-cart-filled]
//   [data-cart-goods|total|delivery|count-text|free-hint]
//   [data-checkout-lines]         — сводка заказа на шаге 3
//   [data-meta][data-meta-key] + [data-meta-value] — сегментные переключатели
//
// Узлы собираются через DOM API, а не innerHTML: названия изделий приходят
// из каталога и могут содержать кавычки, которые ломали бы разметку.

import * as cart from './cart.js';
import * as delivery from './delivery.js';
import * as deliveryUi from './delivery-ui.js';
import * as articles from './articles.js';
import * as fader from './fader.js';
import * as router from './router.js';

const money = cart.formatPrice;

function each(selector, fn, root) {
  (root || document).querySelectorAll(selector).forEach(fn);
}

function el(tag, options, children) {
  const node = document.createElement(tag);
  const opts = options || {};
  if (opts.class) node.className = opts.class;
  if (opts.text != null) node.textContent = opts.text;
  if (opts.attrs) {
    Object.keys(opts.attrs).forEach(k => node.setAttribute(k, opts.attrs[k]));
  }
  (children || []).forEach(child => node.appendChild(child));
  return node;
}

// ── Шапка ───────────────────────────────────────────────────────────────────
function renderBadge() {
  const n = cart.count();
  each('[data-cart-count]', node => {
    node.textContent = n;
    node.hidden = n === 0;
  });
}

// ── Степперы ────────────────────────────────────────────────────────────────
function renderSteppers() {
  each('[data-qty]', node => {
    const slug = node.getAttribute('data-slug');
    const value = node.querySelector('[data-qty-value]');
    const dec = node.querySelector('[data-qty-dec]');
    const articleId = node.getAttribute('data-article') || '';
    // Степпер на шаге «собрать заказ» относится к модели целиком: номер
    // экземпляра там ещё не выбран.
    const qty = articleId ? cart.qtyOf(slug, articleId) : cart.qtyOfProduct(slug);
    if (value) value.textContent = qty;
    if (dec) dec.disabled = qty === 0;
  });
}

function stepper(slug, articleId, name, qty) {
  const plus = el('button', {
    text: '+',
    attrs: { type: 'button', 'data-qty-inc': '', 'aria-label': 'Добавить: ' + name },
  });
  // Экземпляр физически один — увеличивать нечего.
  if (articleId) plus.disabled = qty >= cart.MAX_PER_ARTICLE;

  return el('div', {
    class: 'crb-partial-sm-stepper',
    attrs: { 'data-qty': '', 'data-slug': slug, 'data-article': articleId || '' },
  }, [
    el('button', {
      text: '−',
      attrs: { type: 'button', 'data-qty-dec': '', 'aria-label': 'Убрать: ' + name },
    }),
    el('span', { text: String(qty), attrs: { 'data-qty-value': '', 'aria-live': 'polite' } }),
    plus,
  ]);
}

// ── Строки корзины ──────────────────────────────────────────────────────────
function lineNode(line) {
  const p = line.product;

  // Фотография именно того экземпляра, который выбрали: в корзине должно быть
  // видно, какая физическая вещь заказана.
  const thumb = el('img', {
    class: 'crb-partial-sm-cart-line-photo',
    attrs: {
      src: line.image,
      width: '800',
      height: '1000',
      loading: 'lazy',
      decoding: 'async',
      alt: '',
    },
  });

  const main = el('div', { class: 'crb-partial-sm-cart-line-main' }, [
    el('a', {
      class: 'crb-partial-sm-cart-line-name',
      text: p.name,
      attrs: { href: '/catalog/' + p.slug + '/' },
    }),
    el('p', {
      class: 'crb-partial-sm-cart-line-meta',
      text: line.articleId ? 'Экземпляр № ' + line.articleId : p.category + ' · ручная работа',
    }),
    el('p', { class: 'crb-partial-sm-cart-line-unit', text: money(p.price) }),
  ]);

  const side = el('div', { class: 'crb-partial-sm-cart-line-side' }, [
    stepper(p.slug, line.articleId, p.name, line.qty),
    el('p', { class: 'crb-partial-sm-cart-line-total', text: money(line.total) }),
    el('button', {
      class: 'crb-partial-sm-cart-line-remove',
      text: 'Удалить',
      attrs: {
        type: 'button',
        'data-cart-remove': '',
        'data-slug': p.slug,
        'data-article': line.articleId,
      },
    }),
  ]);

  return el('li', { class: 'crb-partial-sm-cart-line' }, [thumb, main, side]);
}

function renderLines() {
  const host = document.querySelector('[data-cart-lines]');
  if (!host) return;
  host.replaceChildren(...cart.lines().map(lineNode));
}

// Отдельно от renderLines: контейнер [data-cart-lines] есть только на шаге 2,
// и пока переключение жило внутри, шаг 3 выходил из функции раньше времени
// и навсегда оставался в состоянии «корзина пуста».
function renderEmptyState() {
  const isEmpty = cart.lines().length === 0;
  each('[data-cart-empty]', node => { node.hidden = !isEmpty; });
  each('[data-cart-filled]', node => { node.hidden = isEmpty; });
}

// ── Сводка заказа (шаг 3) ───────────────────────────────────────────────────
function renderSummary() {
  const host = document.querySelector('[data-checkout-lines]');
  if (!host) return;

  host.replaceChildren(...cart.lines().map(l =>
    el('div', { class: 'crb-partial-sm-checkout-line' }, [
      // Номер экземпляра — часть заказа: по нему мастер поймёт, какую
      // именно вещь отложить и отправить.
      el('span', {
        text: l.product.name + (l.articleId ? ' — № ' + l.articleId : '') + (l.qty > 1 ? ' × ' + l.qty : ''),
      }),
      el('span', { text: money(l.total) }),
    ])
  ));
}

// ── Итоги ───────────────────────────────────────────────────────────────────
function plural(n, forms) {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return forms[2];
  if (b > 1 && b < 5) return forms[1];
  if (b === 1) return forms[0];
  return forms[2];
}

function renderTotals() {
  const t = cart.totals();

  each('[data-cart-goods]', node => { node.textContent = money(t.goods); });
  each('[data-cart-total]', node => { node.textContent = money(t.total); });

  each('[data-cart-delivery]', node => {
    node.textContent = t.delivery === 0 ? 'бесплатно' : money(t.delivery);
    node.classList.toggle('is-free', t.delivery === 0);
  });

  each('[data-cart-free-hint]', node => {
    if (t.freeReason === 'pickup') {
      node.textContent = '✓ Самовывоз из мастерской — бесплатно.';
    } else if (t.freeReason === 'promo') {
      node.textContent = '✓ Доставка бесплатно — заказ от ' + money(cart.FREE_DELIVERY_FROM) + '.';
    } else {
      node.textContent = 'Стоимость доставки СДЭК посчитаем на шаге оформления.';
    }
    node.classList.toggle('is-free', t.freeDelivery);
  });

  each('[data-cart-count-text]', node => {
    const n = cart.count();
    node.textContent = n + ' ' + plural(n, ['изделие', 'изделия', 'изделий']);
  });
}

// ── Переключатели параметров ────────────────────────────────────────────────
function renderMeta() {
  const meta = cart.state().meta;
  each('[data-meta]', group => {
    const key = group.getAttribute('data-meta-key');
    each('[data-meta-value]', btn => {
      const active = btn.getAttribute('data-meta-value') === meta[key];
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', String(active));
    }, group);
  });
}

function renderAll() {
  renderBadge();
  renderSteppers();
  renderLines();
  renderSummary();
  renderEmptyState();
  renderTotals();
  renderMeta();
  deliveryUi.render();
}

// ── События ─────────────────────────────────────────────────────────────────
function flash(btn, text) {
  if (btn.dataset.flashing) return;
  const original = btn.textContent;
  btn.dataset.flashing = '1';
  btn.textContent = text;
  setTimeout(() => {
    btn.textContent = original;
    delete btn.dataset.flashing;
  }, 1200);
}

function onClick(e) {
  const add = e.target.closest('[data-add-to-cart]');
  if (add) {
    e.preventDefault();
    // Сначала выбор экземпляра — вещь физически одна, и покупатель должен
    // видеть, какую именно берёт.
    const opened = articles.open(add.getAttribute('data-slug'));
    if (!opened) flash(add, 'Нет в наличии');
    return;
  }

  // Ссылки, открывающие окно товара в нужном режиме («Задать вопрос мастеру»).
  // Перехватываем только обычный клик — Ctrl/⌘ и средняя кнопка должны
  // по-прежнему открывать адрес в новой вкладке.
  const openLink = e.target.closest('[data-picker-open][data-slug]');
  if (openLink && !e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) {
    if (articles.open(openLink.getAttribute('data-slug'), openLink.getAttribute('data-picker-open'))) {
      e.preventDefault();
    }
    return;
  }

  // Точки под главным фото товара — ручное переключение экземпляра.
  const dot = e.target.closest('[data-fade-dot]');
  if (dot) {
    const wrap = dot.closest('.crb-partial-sm-product-gallery');
    const stack = wrap && wrap.querySelector('[data-fade]');
    if (stack) {
      fader.show(stack, Number(dot.getAttribute('data-fade-dot')));
      each('[data-fade-dot]', d => d.classList.toggle('is-active', d === dot), wrap);
    }
    return;
  }

  const stepperEl = e.target.closest('[data-qty]');
  if (stepperEl) {
    const slug = stepperEl.getAttribute('data-slug');
    const articleId = stepperEl.getAttribute('data-article') || '';
    if (e.target.closest('[data-qty-inc]')) return cart.setQty(slug, articleId, cart.qtyOf(slug, articleId) + 1);
    if (e.target.closest('[data-qty-dec]')) return cart.setQty(slug, articleId, cart.qtyOf(slug, articleId) - 1);
  }

  const rm = e.target.closest('[data-cart-remove]');
  if (rm) return cart.remove(rm.getAttribute('data-slug'), rm.getAttribute('data-article') || '');

  const metaBtn = e.target.closest('[data-meta-value]');
  if (metaBtn) {
    const group = metaBtn.closest('[data-meta]');
    if (group) cart.setMeta(group.getAttribute('data-meta-key'), metaBtn.getAttribute('data-meta-value'));
  }
}

export function init() {
  document.addEventListener('click', onClick);
  cart.subscribe(renderAll);
  renderAll();
  deliveryUi.init(renderAll);
  articles.init();
  fader.init();

  // Адреса окон: /catalog/<slug>/select|corporate|masterclass|<id>, /contact/.
  // Модальные окна живут в разметке, поэтому роутер только показывает нужное.
  const contactRoot = document.querySelector('.crb-partial-sm-contact');
  router.init({
    openPicker: state => articles.openFromRoute(state),
    closePicker: () => articles.close(),
    openContact: () => {
      // Как и окно выбора: ждём, пока библиотека поднимет модалку.
      const wait = (tries = 40) => {
        if (contactRoot && contactRoot._modal) return contactRoot._modal.show();
        if (tries > 0) setTimeout(() => wait(tries - 1), 100);
      };
      wait();
    },
    closeContact: () => { if (contactRoot && contactRoot._modal) contactRoot._modal.hide(); },
  });

  // Адрес окна «Написать» ведём по факту открытия, а не по клику: закрыть его
  // можно и крестиком, и Esc, и по подложке — все эти пути идут через класс.
  if (contactRoot) {
    let wasOpen = contactRoot.classList.contains('is-open');
    new MutationObserver(() => {
      const open = contactRoot.classList.contains('is-open');
      if (open === wasOpen) return;
      wasOpen = open;
      router.pushState(open ? 'contact' : 'none');
    }).observe(contactRoot, { attributes: true, attributeFilter: ['class'] });
  }
}
